import { useEffect, useState } from 'react';
import { api } from './lib/api';
import type { Melody } from './lib/types';
import { logger } from './lib/logger';
import MelodyPlayer from './components/MelodyPlayer';
// import { dummyMelodies } from './lib/dummyData';

interface CurrentMelody {
  melody: Melody;
  index: number;
}

function App() {
  const [currentMelody, setCurrentMelody] = useState<CurrentMelody | null>(null);
  const [nextMelodies, setNextMelodies] = useState<Melody[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNext, setLoadingNext] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [melodyIndices, setMelodyIndices] = useState<number[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Log state changes
  useEffect(() => {
    console.log('State Update:', {
      currentMelody,
      nextMelodies,
      loading,
      error,
      melodyIndices,
      totalCount,
      currentIndex
    });
  }, [currentMelody, nextMelodies, loading, error, melodyIndices, totalCount, currentIndex]);

  // Initial load
  useEffect(() => {
    console.log('Initial load...');
    const initialize = async () => {
      try {
        await loadLastMelody();
        await loadNextMelodies();
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  async function loadLastMelody() {
    try {
      const response = await api.getLastMelody();
      console.log('Last Melody Response:', response);
      setTotalCount(response.totalMelodies);
      
      // Temporary: Use dummy data
      // console.log('Using dummy data for last melody');
      // setTotalCount(dummyMelodies.length);
    } catch (e) {
      logger.error('Error loading last melody', { error: e });
      setError('Failed to load melody data');
    }
  }

  const transformMelody = (melody: Melody): Melody => {
    return melody.map(note => {
      if (note.includes("'")) {
        const baseNote = note.replace("'", "");
        // If the note doesn't end with a number, add octave 5
        if (!baseNote.match(/\d$/)) {
          return baseNote + '5';
        }
        // If it does have a number, increment it
        const octave = parseInt(baseNote.slice(-1)) + 1;
        return baseNote.slice(0, -1) + octave;
      }
      if (!note.match(/\d$/)) {
        return note + '4';
      }
      return note;
    });
  };

  async function loadNextMelodies() {
    try {
      setLoadingNext(true);
      setError(null);
      console.log('Loading next melodies...');
      
      const response = await api.getNextMelodies(50);
      
      console.log('Next Melodies Response:', response);
      
      // Transform melodies to include octaves
      const transformedMelodies = response.melodies.map(transformMelody);
      
      setNextMelodies(transformedMelodies);
      setMelodyIndices(response.indices);
      if (transformedMelodies.length > 0) {
        const newCurrentMelody = {
          melody: transformedMelodies[0],
          index: response.indices[0]
        };
        console.log('Setting current melody:', newCurrentMelody);
        setCurrentMelody(newCurrentMelody);
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to load melodies';
      console.error('Error loading melodies:', e);
      setError(errorMessage);
    } finally {
      setLoadingNext(false);
    }
  }

  const formatDuration = (seconds: number): string => {
    const years = Math.floor(seconds / (365 * 24 * 3600));
    const days = Math.floor((seconds % (365 * 24 * 3600)) / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);

    return parts.join(' ');
  };

  const calculateTimeToEnd = (): string => {
    if (!currentMelody || totalCount === 0) return '0s';
    
    const secondsPerNote = 0.5; // 500ms per note
    const secondsPerMelody = 8 * secondsPerNote; // 8 notes per melody
    const pauseBetweenMelodies = 0.5; // 500ms pause
    const remainingMelodies = totalCount - currentIndex;
    const totalSeconds = (remainingMelodies * secondsPerMelody) + ((remainingMelodies - 1) * pauseBetweenMelodies);
    
    console.log('Time calculation:', {
      currentIndex,
      melodyIndex: currentMelody.index,
      totalCount,
      remainingMelodies,
      totalSeconds,
      secondsPerMelody,
      pauseBetweenMelodies
    });
    
    return formatDuration(totalSeconds);
  };

  const handlePlaybackComplete = () => {
    console.log('Playback completed, updating melody list...');
    setCurrentIndex(prev => prev + 1);
    
    // Remove the completed melody and update the current melody
    const updatedMelodies = nextMelodies.slice(1);
    const updatedIndices = melodyIndices.slice(1);
    
    if (updatedMelodies.length > 0) {
      setNextMelodies(updatedMelodies);
      setMelodyIndices(updatedIndices);
      const newCurrentMelody = {
        melody: updatedMelodies[0],
        index: updatedIndices[0]
      };
      setCurrentMelody(newCurrentMelody);
      // Keep playing by setting isPlaying to true
      setIsPlaying(true);
    } else {
      // If no more melodies, load new ones
      loadNextMelodies().then(() => {
        // Start playing the new melodies
        setIsPlaying(true);
      });
    }
  };

  // Update currentIndex when currentMelody changes
  useEffect(() => {
    if (currentMelody) {
      setCurrentIndex(currentMelody.index);
    }
  }, [currentMelody]);

  const formatNote = (note: string): string => {
    if (!note.match(/\d$/)) {
      return note + '4';
    }
    return note;
  };

  return (
    <main className="container mx-auto px-4 py-8 min-h-screen">
      <header>
        <h1 className="text-4xl font-bold mb-8 text-center">Infinite Melodies</h1>
      </header>

      {error ? (
        <section className="text-center text-red-600">
          <p className="text-lg">{error}</p>
        </section>
      ) : (
        <article className="max-w-2xl mx-auto">
          <section>
            <MelodyPlayer 
              melody={currentMelody?.melody ?? []}
              iteration={currentMelody?.index ?? 0}
              remainingCount={totalCount}
              isPlaying={isPlaying}
              timeToEnd={calculateTimeToEnd()}
              onPlaybackComplete={handlePlaybackComplete}
              isLoading={loading}
              melodyIndex={currentMelody?.index ?? 0}
            />
            <div className="mt-4">
              {isPlaying ? (
                <button
                  onClick={() => setIsPlaying(false)}
                  className="w-full px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white"
                  aria-label="Stop playing"
                >
                  Stop Playing
                </button>
              ) : (
                <button
                  onClick={() => setIsPlaying(true)}
                  disabled={loading}
                  className={`w-full px-4 py-2 rounded-lg ${
                    loading 
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                  aria-label={loading ? "Loading" : "Play melody"}
                >
                  {loading ? "Loading" : "Play Melodies"}
                </button>
              )}
            </div>
          </section>
          
          <section className="mt-8">
            <header>
              <h2 className="text-2xl font-semibold mb-4">Next Melodies</h2>
            </header>
            <section>
              <ul className="grid grid-cols-1 gap-4">
                {loadingNext ? (
                  [...Array(5)].map((_, i) => (
                    <li key={i}>
                      <div className="h-16 bg-gray-700 rounded-lg animate-pulse"></div>
                    </li>
                  ))
                ) : (
                  nextMelodies.slice(1, 6).map((melody, index) => (
                    <li key={`${melodyIndices[index + 1]}-${currentMelody?.index}`}>
                      <div className={`w-full p-4 border rounded-lg ${
                        currentMelody?.index === melodyIndices[index + 1] ? 'bg-blue-50 border-blue-500' : ''
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Melody {melodyIndices[index + 1]}</span>
                          <span className="text-gray-300">{melody.map(formatNote).join(' - ')}</span>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
              <p className="mt-2 text-center text-sm text-gray-400">
                {(() => {
                  const remaining = Math.max(totalCount - currentIndex - 1, 0);
                  return remaining === 0 
                    ? "You've reached the end of the melodies!"
                    : `Only ${remaining.toLocaleString()} more melodies to explore...`;
                })()}
              </p>
            </section>
          </section>

          <section className="mt-12 p-6 bg-gray-800 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4">About Infinite Melodies</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                There are approximately 26.9 million possible 7-note melodies within a single octave. That number can be calculated by the formula below:
              </p>
              <p className="text-center font-mono text-sm">
                s^n - (s-1)^n
              </p>
              <p className="text-sm text-gray-400 text-center">
                Source: <a href="https://plus.maths.org/content/how-many-melodies-are-there" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-300">How many melodies are there? - Plus Magazine</a>
              </p>
              <p>Infinite Melodies is a collaborative project. Every time someone listens to a complete melody, it moves on to the next one.</p>
              <p>
                Press play and lets get through all 26.9 million possibilities together.
              </p>
            </div>
          </section>
        </article>
      )}
    </main>
  );
}

export default App; 